import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { RefreshCcw } from 'lucide-react';
import { deriveMetrics, type MetricValues } from '@marketing-os/shared';
import { formatDate, formatMoney, formatNumber, formatPct, lastNDays, type Period } from '@/lib/format';
import { BarChartCard } from '@/components/charts/BarChartCard';
import { LineChartCard } from '@/components/charts/LineChartCard';
import { StatTile } from '@/components/charts/StatTile';
import { PageHeader } from '@/components/common/PageHeader';
import { ErrorState } from '@/components/common/QueryState';
import { Button } from '@/components/ui/Button';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { EmptyState } from '@/components/ui/EmptyState';
import { SkeletonCards } from '@/components/ui/Skeleton';
import { Tabs } from '@/components/ui/Tabs';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/stores/auth.store';
import { useUiStore } from '@/stores/ui.store';
import { Permission } from '@marketing-os/shared';
import { useClient } from '@/features/clients/api';
import type { MetricBreakdownRow } from '@/types/api';
import { useClientDashboard, useSyncMetrics } from './api';

const BREAKDOWN_TABS = [
  { id: 'byCity', label: 'Cidade' },
  { id: 'byPlan', label: 'Plano' },
  { id: 'byCreative', label: 'Criativo' },
  { id: 'byAudience', label: 'Público' },
  { id: 'byKeyword', label: 'Palavra-chave' },
] as const;

type BreakdownKey = (typeof BREAKDOWN_TABS)[number]['id'];

export interface ClientDashboardPageProps {
  /** Quando usado no portal do cliente, o id vem da sessão. */
  fixedClientId?: string;
  hideHeaderActions?: boolean;
}

export function ClientDashboardPage({ fixedClientId, hideHeaderActions }: ClientDashboardPageProps) {
  const params = useParams<{ clientId?: string }>();
  const selectedClientId = useUiStore((state) => state.selectedClientId);
  const clientId = fixedClientId ?? params.clientId ?? selectedClientId ?? undefined;

  const [period, setPeriod] = useState<Period>(() => lastNDays(30));
  const [breakdown, setBreakdown] = useState<BreakdownKey>('byCity');
  const { can } = useAuth();
  const toast = useToast();

  const { data: client } = useClient(clientId);
  const { data, isLoading, isError, error, refetch } = useClientDashboard(clientId, period);
  const syncMetrics = useSyncMetrics();

  if (!clientId) {
    return (
      <div className="surface-card">
        <EmptyState
          title="Nenhum cliente selecionado"
          description="Escolha um cliente no seletor do topo para ver o painel."
        />
      </div>
    );
  }

  const totals: MetricValues | undefined = data?.totals;
  const derived = data?.derived ?? (totals ? deriveMetrics(totals) : undefined);
  const previous = data?.previous;

  const series =
    data?.series.map((point) => ({
      bucket: point.bucket,
      investimento: point.values.spendCents / 100,
      leads: point.values.leads,
      cliques: point.values.clicks,
    })) ?? [];

  const rows: MetricBreakdownRow[] = data?.breakdowns?.[breakdown] ?? [];

  return (
    <>
      <PageHeader
        title={client ? `Painel — ${client.name}` : 'Painel do cliente'}
        description="Investimento, alcance, conversões e qualidade de lead no período selecionado."
        actions={
          hideHeaderActions ? (
            <DateRangePicker value={period} onChange={setPeriod} className="w-full sm:w-72" />
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <DateRangePicker value={period} onChange={setPeriod} className="w-full sm:w-72" />
              {can(Permission.METRIC_READ) && (
                <Button
                  variant="outline"
                  leftIcon={<RefreshCcw className="h-4 w-4" />}
                  loading={syncMetrics.isPending}
                  onClick={() =>
                    syncMetrics.mutate(clientId, {
                      onSuccess: () =>
                        toast.info('Sincronização enfileirada', 'As métricas serão atualizadas em instantes.'),
                      onError: () => toast.error('Não foi possível enfileirar a sincronização'),
                    })
                  }
                >
                  Sincronizar
                </Button>
              )}
            </div>
          )
        }
      />

      {isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : isLoading ? (
        <div className="space-y-5">
          <SkeletonCards count={8} />
        </div>
      ) : !data || !totals || !derived ? (
        <div className="surface-card">
          <EmptyState
            title="Sem métricas no período"
            description="Nenhum dado foi sincronizado para este cliente no intervalo selecionado."
          />
        </div>
      ) : (
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatTile
              label="Investimento"
              value={formatMoney(totals.spendCents)}
              current={totals.spendCents}
              previous={previous?.totals.spendCents}
            />
            <StatTile
              label="Impressões"
              value={formatNumber(totals.impressions)}
              current={totals.impressions}
              previous={previous?.totals.impressions}
            />
            <StatTile
              label="Alcance"
              value={formatNumber(totals.reach)}
              current={totals.reach}
              previous={previous?.totals.reach}
              footer={`Frequência média ${totals.frequency.toFixed(2).replace('.', ',')}`}
            />
            <StatTile
              label="Cliques"
              value={formatNumber(totals.clicks)}
              current={totals.clicks}
              previous={previous?.totals.clicks}
            />
            <StatTile
              label="CTR"
              value={formatPct(derived.ctr)}
              current={derived.ctr}
              previous={previous?.derived.ctr}
            />
            <StatTile
              label="CPC"
              value={formatMoney(derived.cpcCents)}
              current={derived.cpcCents}
              previous={previous?.derived.cpcCents}
              lowerIsBetter
            />
            <StatTile
              label="CPM"
              value={formatMoney(derived.cpmCents)}
              current={derived.cpmCents}
              previous={previous?.derived.cpmCents}
              lowerIsBetter
            />
            <StatTile
              label="Leads"
              value={formatNumber(totals.leads)}
              current={totals.leads}
              previous={previous?.totals.leads}
            />
            <StatTile
              label="CPL"
              value={formatMoney(derived.cplCents)}
              current={derived.cplCents}
              previous={previous?.derived.cplCents}
              lowerIsBetter
            />
            <StatTile
              label="Conversões"
              value={formatNumber(totals.conversions)}
              current={totals.conversions}
              previous={previous?.totals.conversions}
            />
            <StatTile
              label="Taxa de conversão"
              value={formatPct(derived.conversionRate)}
              current={derived.conversionRate}
              previous={previous?.derived.conversionRate}
            />
            <StatTile
              label="Custo por conversão"
              value={formatMoney(derived.costPerConversionCents)}
              current={derived.costPerConversionCents}
              previous={previous?.derived.costPerConversionCents}
              lowerIsBetter
            />
            <StatTile label="Ligações" value={formatNumber(totals.calls)} />
            <StatTile label="Conversas no WhatsApp" value={formatNumber(totals.messagingConversations)} />
            <StatTile label="Formulários enviados" value={formatNumber(totals.formSubmissions)} />
            <StatTile
              label="Custo por lead qualificado"
              value={data.quality ? formatMoney(data.quality.costPerQualifiedLeadCents) : '—'}
              lowerIsBetter
              footer={
                data.quality
                  ? `${formatNumber(data.quality.qualifiedLeads)} leads qualificados`
                  : 'Sem dados de qualificação no período'
              }
            />
            <StatTile
              label="Custo por instalação"
              value={data.quality ? formatMoney(data.quality.costPerInstallCents) : '—'}
              lowerIsBetter
              footer={
                data.quality
                  ? `${formatNumber(data.quality.installs)} instalações`
                  : 'Integre o CRM para acompanhar instalações'
              }
            />
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <LineChartCard
              title="Evolução diária de investimento"
              description="Comparação de gasto ao longo do período."
              data={series}
              xKey="bucket"
              xTickFormatter={(value) => formatDate(value, 'dd/MM')}
              yTickFormatter={(value) => formatMoney(value * 100)}
              series={[{ key: 'investimento', label: 'Investimento', formatter: (v) => formatMoney(v * 100) }]}
            />
            <LineChartCard
              title="Leads e cliques por dia"
              description="Volume de interesse gerado ao longo do período."
              data={series}
              xKey="bucket"
              xTickFormatter={(value) => formatDate(value, 'dd/MM')}
              yTickFormatter={(value) => formatNumber(value)}
              series={[
                { key: 'leads', label: 'Leads', formatter: formatNumber },
                { key: 'cliques', label: 'Cliques', formatter: formatNumber },
              ]}
            />
          </div>

          <section>
            <h2 className="mb-2 text-sm font-semibold text-fg">Desempenho por dimensão</h2>
            <Tabs
              items={BREAKDOWN_TABS.map((tab) => ({ id: tab.id, label: tab.label }))}
              value={breakdown}
              onChange={(id) => setBreakdown(id as BreakdownKey)}
            >
              <BarChartCard
                title={`Investimento e leads por ${BREAKDOWN_TABS.find((t) => t.id === breakdown)?.label.toLowerCase()}`}
                data={rows.map((row) => ({
                  label: row.label,
                  investimento: row.spendCents / 100,
                  leads: row.leads,
                }))}
                xKey="label"
                layout="vertical"
                height={Math.max(240, rows.length * 40)}
                yTickFormatter={(value) => formatNumber(value)}
                series={[
                  { key: 'investimento', label: 'Investimento (R$)', formatter: (v) => formatMoney(v * 100) },
                  { key: 'leads', label: 'Leads', formatter: formatNumber },
                ]}
                emptyMessage="Sem quebra disponível para esta dimensão no período."
              />
            </Tabs>
          </section>
        </div>
      )}
    </>
  );
}
